import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Deep-link entry for a single material/resource stream post (the link a teacher
 * copies from a Resource post): /m/<classId>/<postId>
 *
 * Mirrors AssignmentDeepLink exactly — same auto-join behavior — but focuses a
 * stream POST instead of an assessment:
 *   - signed out       -> clean, class-specific "Continue with Google" screen.
 *                         New accounts are created as students automatically,
 *                         then land on the material.
 *   - student/nickname -> student portal focused on this post (the portal joins
 *                         them to the class if they aren't enrolled).
 *   - teacher/admin     -> the app (no student-facing focus view for them).
 *
 * Returning students stay signed in (Firebase persistence), so future clicks go
 * straight to the material with no prompt.
 */
export const MaterialDeepLink: React.FC = () => {
  const { classId, postId } = useParams();
  const { currentUser, userProfile, loading, startGoogleLogin, completeGoogleLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }
  if (!classId || !postId) return <Navigate to="/" replace />;

  // Signed in — route by role.
  if (currentUser) {
    if (!userProfile && !currentUser.isAnonymous) {
      return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
    }
    if (userProfile?.role === 'student' || currentUser.isAnonymous) {
      return <Navigate to={`/student?openClass=${encodeURIComponent(classId)}&focusPost=${encodeURIComponent(postId)}`} replace />;
    }
    return <Navigate to="/app" replace />;
  }

  // Signed OUT — show a focused, Google-first join screen (not the generic login).
  const handleGoogle = async () => {
    setBusy(true);
    setError(null);
    // Fallback target in case the provider uses a full-page redirect flow.
    sessionStorage.setItem('deepLink', `/m/${classId}/${postId}`);
    try {
      const { needsRole } = await startGoogleLogin();
      // Brand-new accounts arriving via a material link are students.
      if (needsRole) await completeGoogleLogin('student');
      // currentUser updates → this component re-renders into the routing above.
    } catch {
      setError("Sign-in didn't complete. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#59A8DA] to-[#5FA660] flex items-center justify-center text-white font-black text-lg mb-4">
          LUC
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Open your class material</h1>
        <p className="text-sm text-gray-500 mb-6">
          Sign in with your school Google account to join the class and open this material in Level Up Classroom.
        </p>
        <button
          onClick={handleGoogle}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          {busy ? 'Signing in…' : 'Continue with Google'}
        </button>
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        <p className="text-[11px] text-gray-400 mt-5">You'll go straight to your material after signing in.</p>
      </div>
    </div>
  );
};
